import React, { useEffect, useRef, useState } from "react";
import { Cap, Level } from "../../../utils/rbacCatalog";
import { AiOutlineLoading } from "react-icons/ai";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import DlSearch from "./DlSearch";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import useAuth from "../../../hooks/useAuth";
import Plans from "../../Plans";
import { PlanTier, usePermissions } from "../../../utils/permissions";
import { i18n, TFunction } from "i18next";
import { IUser } from "../../../contexts/types";
import { Dropdown } from "flowbite-react";
import { AlertMessage } from "../../atoms/AlertMessage";
import NoTranslate from "../../atoms/NoTranslate";
import PromoCodeModal from "../../modals/PromoCodeModal";
import {
  HiChartBar,
  HiChevronDown,
  HiCurrencyDollar,
  HiGlobeAlt,
  HiLogin,
  HiOutlineCalendar,
  HiOutlineCog,
  HiOutlineFilter,
  HiOutlineViewBoards,
  HiOutlineGift,
  HiOutlineHome,
  HiOutlineIdentification,
  HiOutlineUsers,
  HiOutlineViewGridAdd,
  HiShieldCheck,
  HiSparkles,
  HiChip,
  HiSupport,
  HiTrendingUp,
  HiUser,
  HiX,
  HiPencilAlt,
  HiExclamation,
  HiUserGroup,
} from "react-icons/hi";
import { HiTicket } from "react-icons/hi2";
import { BiDollar } from "react-icons/bi";
import { CustomButton } from "../../atoms/CustomButton";
import { ModalShell } from "../../atoms/ModalShell";
import { useGetEntityPreferencesByPath } from "../../../hooks/entity";
import { useGetVerifyPlans } from "../../../hooks/billing";
import { useGetCountUsers } from "../../../hooks/users";
import { useHomeAttention } from "../../../hooks/useHomeAttention";
import { useDashboardLanguage } from "../../../hooks/useDashboardLanguage";
import SecureStorage from "../../../utils/secureStorage";
import {
  trackSubscriptionConversion,
  trackPanelView,
} from "../../../services/conversionTracking";
import { CH, FR, GB, US } from "country-flag-icons/react/3x2";
import { MdOutlineMonitorHeart } from "react-icons/md";
import { HiOutlineUserPlus } from "react-icons/hi2";
import { useCookies } from "react-cookie";
import {
  LANGUAGE_STORAGE_KEY,
  getCookieDomain,
  getLanguageCode,
  persistLanguageToStorage,
} from "../../../utils/languageUtility";
import ImpersonationBanner from "../../molecules/ImpersonationBanner";

type SideParMenuProps = {
  t: TFunction<"translation", undefined, "translation">;
  handleUpgrade: VoidFunction;
  handleHideSidebar: VoidFunction;
  i18n: i18n;
  user: IUser;
  show: boolean;
  navigate: ReturnType<typeof useNavigate>;
  customer_identification: string;
  entityIdentification: string;
};

const SideBarMenu = ({
  t,
  handleUpgrade,
  handleHideSidebar,
  user,
  show,
  i18n,
  navigate,
  customer_identification,
  entityIdentification,
}: SideParMenuProps) => {
  const sideBarRef = useRef<HTMLElement | null>(null);
  const permissions = usePermissions(user);
  const isRootUser = permissions.isRootUser();
  const canReadCustomers = permissions.can(Cap.CUSTOMERS_LIST, Level.VIEW);
  const { data: countData } = useGetCountUsers(
    customer_identification,
    entityIdentification,
    { enabled: canReadCustomers }
  );

  const homeAttention = useHomeAttention(
    customer_identification,
    entityIdentification,
    user,
    { enabled: !isRootUser }
  );

  const showHomeWarningDot = !isRootUser && homeAttention.needsAttention;

  const reasonLabels: Record<string, string> = {
    senderEmail: t(
      "dashboard.entities.emailSenderValidation.sidebarHomeReasonSenderEmail"
    ) as string,
    smsCredit: t(
      "dashboard.entities.emailSenderValidation.sidebarHomeReasonSmsCredit"
    ) as string,
  };

  const homeAttentionTitle = showHomeWarningDot
    ? (t("dashboard.entities.emailSenderValidation.sidebarHomeNeedsAttention", {
        reasons: homeAttention.reasons
          .map((r) => reasonLabels[r])
          .filter(Boolean)
          .join(", "),
      }) as string)
    : "";

  type MenuLink = {
    title: string;
    href: string;
    icon: React.ReactNode;
    isEnd: boolean;
    isAccessible: boolean;
    attentionDot?: boolean;
    attentionTitle?: string;
  };

  const menuLinks: MenuLink[] = [
    {
      title: t("dashboard.layout.item-01"),
      href: "/control-panel",
      icon: <HiOutlineHome size={22} />,
      isEnd: true,
      isAccessible: !isRootUser,
      attentionDot: showHomeWarningDot,
      attentionTitle: homeAttentionTitle,
    },
    {
      title: t("leads.sidebarTitle"),
      href: "leads",
      icon: <HiOutlineFilter size={22} />,
      isEnd: true,
      isAccessible: !isRootUser && permissions.can(Cap.LEADS, Level.VIEW),
    },
    {
      title: t("dashboard.layout.item-02"),
      href: "users",
      icon: <HiOutlineUsers size={22} />,
      isEnd: true,
      isAccessible:
        !isRootUser && permissions.can(Cap.CUSTOMERS_LIST, Level.VIEW),
    },
    {
      title: t("dashboard.layout.item-03"),
      href: "groups",
      icon: <HiOutlineViewGridAdd size={22} />,
      isEnd: true,
      isAccessible:
        !isRootUser && permissions.can(Cap.GROUP_MANAGEMENT, Level.VIEW),
    },
    {
      title: t("dashboard.layout.item-16"),
      href: "signup-forms",
      icon: <HiOutlineUserPlus size={22} />,
      isEnd: true,
      isAccessible:
        !isRootUser && permissions.can(Cap.SIGNUP_FORMS, Level.VIEW),
    },
    {
      title: t("dashboard.layout.item-12"),
      href: "login-template",
      icon: <HiGlobeAlt size={22} />,
      isEnd: true,
      isAccessible: !isRootUser && permissions.can(Cap.APIS, Level.VIEW),
    },
    {
      title: t("dashboard.features.lmc.page.title"),
      href: "lmc",
      icon: <HiOutlineGift size={22} />,
      isEnd: true,
      isAccessible: !isRootUser && permissions.can(Cap.LOYALTY, Level.VIEW),
    },
    {
      title: t("dashboard.layout.item-15"),
      href: "business-card",
      icon: <HiOutlineIdentification size={22} />,
      isEnd: true,
      isAccessible:
        !isRootUser && permissions.can(Cap.BUSINESS_CARDS, Level.VIEW),
    },
    {
      title: t("dashboard.layout.item-18"),
      href: "event-payments",
      icon: <HiTicket size={22} />,
      isEnd: true,
      isAccessible: !isRootUser && permissions.can(Cap.EVENTS, Level.VIEW),
    },
    {
      title: t("agenda.sidebarTitle"),
      href: "agenda",
      icon: <HiOutlineCalendar size={22} />,
      isEnd: true,
      isAccessible:
        !isRootUser && permissions.can(Cap.USERS_AGENDA, Level.VIEW),
    },
    {
      title: t("todos.sidebarTitle"),
      href: "todos",
      icon: <HiOutlineViewBoards size={22} />,
      isEnd: false,
      isAccessible:
        !isRootUser && permissions.can(Cap.TASKS_BOARDS, Level.VIEW),
    },
    {
      title: t("dashboard.layout.item-19"),
      href: "e-signature",
      icon: <HiPencilAlt size={22} />,
      isEnd: true,
      isAccessible:
        !isRootUser && permissions.can(Cap.ESIGNATURE_CONFIG, Level.VIEW),
    },
    {
      title: t("dashboard.layout.item-17"),
      href: "payment-module",
      icon: <HiCurrencyDollar size={22} />,
      isEnd: true,
      isAccessible:
        !isRootUser && permissions.can(Cap.PAYMENT_CONFIG, Level.VIEW),
    },
    {
      title: t("dashboard.aiConfig.menuItem"),
      href: "ai-config",
      icon: <HiSparkles size={22} />,
      isEnd: true,
      isAccessible:
        !isRootUser && permissions.can(Cap.AI_ENGINE_CONFIG, Level.VIEW),
    },
    {
      title: t("dashboard.layout.item-04"),
      href: "settings",
      icon: <HiOutlineCog size={22} />,
      isEnd: true,
      isAccessible:
        !isRootUser &&
        (permissions.can(Cap.ENTITY_SETTINGS, Level.VIEW) ||
          permissions.can(Cap.APIS, Level.VIEW) ||
          permissions.can(Cap.ROLE_MANAGEMENT, Level.VIEW) ||
          permissions.can(Cap.EXPORT_DATA, Level.VIEW)),
    },
    {
      title: "SUM",
      href: "monitoring",
      icon: <HiChartBar size={22} />,
      isEnd: true,
      isAccessible: isRootUser,
    },
    {
      title: "System",
      href: "system",
      icon: <MdOutlineMonitorHeart size={22} />,
      isEnd: true,
      isAccessible: isRootUser,
    },
    {
      title: "Providers",
      href: "providers",
      icon: <HiGlobeAlt size={22} />,
      isEnd: true,
      isAccessible: isRootUser,
    },
    {
      title: "Paymod",
      href: "paymod",
      icon: <HiCurrencyDollar size={22} />,
      isEnd: true,
      isAccessible: isRootUser,
    },
    {
      title: "Partners",
      href: "partners",
      icon: <HiUserGroup size={22} />,
      isEnd: true,
      isAccessible: isRootUser,
    },
    {
      title: "AI Engine",
      href: "ai-engine-monitoring",
      icon: <HiChip size={22} />,
      isEnd: true,
      isAccessible: isRootUser,
    },
    {
      title: "Security",
      href: "security",
      icon: <HiShieldCheck size={22} />,
      isEnd: true,
      isAccessible: isRootUser,
    },
    {
      title: "Tasks",
      href: "todos",
      icon: <HiOutlineViewBoards size={22} />,
      isEnd: true,
      isAccessible: isRootUser,
    },
  ];

  useEffect(() => {
    const handler = (e: any) => {
      if (sideBarRef.current && !sideBarRef.current.contains(e.target)) {
        handleHideSidebar();
      }
    };

    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [handleHideSidebar]);

  useEffect(() => {
    const handleResize = () => {
      handleHideSidebar();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleHideSidebar]);

  useEffect(() => {
    if (sideBarRef.current) {
      const links = sideBarRef.current.querySelectorAll("a");

      links.forEach((link) => {
        link.addEventListener("click", (e) => {
          if (show) handleHideSidebar();
        });
      });
    }
  }, [handleHideSidebar, show]);

  useEffect(() => {
    show
      ? document.body.classList.add("menu-opened")
      : document.body.classList.remove("menu-opened");
  }, [show]);

  return (
    <aside
      ref={sideBarRef}
      className={`pt-5 pb-4 bg-gray-800 flex flex-col justify-start items-start gap-5 lg:w-[320px] md:w-[80px] w-[320px] ${
        show ? "absolute left-0 top-0 bottom-0 z-[2]" : "md:inline-flex hidden"
      }`}
    >
      <div className="self-stretch px-2 flex-col justify-start items-start flex">
        <div className="lg:justify-start md:justify-center justify-start items-center gap-2 flex w-full overflow-hidden">
          <img
            src={"/images/logo.png"}
            alt="Logo"
            className="w-8 h-8 shrink-0"
          />

          <span className="text-white text-xl truncate font-bold font-figtree leading-normal md:hidden lg:block">
            Super User Manager
          </span>

          {show && (
            <button
              className="ml-auto w-7 h-7 p-1 rounded-full hover:bg-gray-700 transition-colors flex-col justify-center items-center inline-flex shrink-0"
              type="button"
              onClick={handleHideSidebar}
            >
              <HiX size={18} className="text-white" />
            </button>
          )}
        </div>
      </div>

      <div className="self-stretch grow shrink basis-0 px-2 flex-col justify-between items-start flex">
        <div className="self-stretch flex-col justify-start items-start gap-4 flex">
          <div className="self-stretch flex-col justify-start items-start gap-1 flex">
            {menuLinks.map((item, index) => {
              if (item.isAccessible)
                return (
                  <NavLink
                    key={index}
                    to={item.href}
                    end={item.isEnd}
                    title={item.attentionDot ? item.attentionTitle : undefined}
                    className={({ isActive }) =>
                      "self-stretch lg:pl-2 lg:pr-3 md:py-1.5 py-1.5 md:pl-1 md:pr-1 pl-2 pr-3 rounded-md lg:justify-start md:justify-center justify-start items-center inline-flex transition-colors" +
                      (isActive
                        ? " text-white bg-gray-900"
                        : " text-gray-300 hover:bg-gray-700 hover:text-white")
                    }
                  >
                    <div className="justify-start items-center lg:gap-3 gap-1 flex lg:flex-row md:flex-col flex-row w-full">
                      <div className="w-6 h-6 relative flex justify-center items-center flex-shrink-0">
                        {item.icon}
                      </div>
                      {item.attentionDot && (
                        <HiExclamation
                          size={16}
                          className="text-amber-500 shrink-0 blink lg:hidden md:block hidden"
                          aria-label={item.attentionTitle}
                        />
                      )}
                      <div className="w-full text-sm font-normal font-figtree leading-normal text-left whitespace-nowrap lg:flex md:hidden flex items-center gap-1.5">
                        <span>{item.title}</span>
                        {item.attentionDot && (
                          <HiExclamation
                            size={18}
                            className="text-amber-500 shrink-0 blink"
                            aria-hidden="true"
                          />
                        )}
                        {(item.href === "users" || item.href === "groups") && (
                          <span className="ml-auto shrink-0 text-xs tabular-nums text-gray-400">
                            {item.href === "users"
                              ? countData?.users
                              : countData?.groups}
                          </span>
                        )}
                      </div>
                    </div>
                  </NavLink>
                );
              else return null;
            })}
          </div>

          <div className="self-stretch flex-col justify-start items-start gap-2 flex">
            {!permissions.isRoleGoverned() && (
              <button
                data-testid="sidebar-upgrade-cta"
                className="self-stretch lg:pl-2 lg:pr-3 md:pl-1 md:pr-1 pl-2 pr-3 py-1.5 bg-gray-700 rounded-md shadow-sm flex items-center lg:gap-3 gap-1 w-full lg:flex-row md:flex-col flex-row"
                onClick={handleUpgrade}
              >
                <div className="w-6 h-6 relative flex-shrink-0 flex justify-center items-center">
                  <HiTrendingUp size={22} className="text-white" />
                </div>
                <div className="text-white text-sm font-medium font-figtree leading-normal text-left whitespace-nowrap lg:block md:hidden block">
                  {t("dashboard.layout.item-10")}
                </div>
              </button>
            )}

            {permissions.isAtLeastTier(PlanTier.PREMIUM) &&
              !permissions.borrowsOwnerPlan && (
                <button
                  className="self-stretch lg:pl-2 lg:pr-3 md:pl-1 md:pr-1 pl-2 pr-3 py-1.5 bg-blue-600 rounded-md shadow-sm flex items-center lg:gap-3 gap-1 w-full lg:flex-row md:flex-col flex-row"
                  onClick={() => navigate("roi-guarantee")}
                >
                  <div className="w-6 h-6 relative flex-shrink-0 flex justify-center items-center">
                    <HiShieldCheck size={22} color="white" />
                  </div>
                  <div className="text-white text-sm font-medium font-figtree leading-normal text-left whitespace-nowrap lg:block md:hidden block">
                    {t("dashboard.roi_guarantee.setup_pack")}
                    {t("dashboard.roi_guarantee.support_pack")}
                  </div>
                </button>
              )}
            {permissions.hasFeatureAccess("BUSINESS_PARTNER_PANEL") &&
              !permissions.borrowsOwnerPlan && (
                <a
                  href="https://bp-panel.super-user-manager.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <button className="self-stretch lg:pl-2 lg:pr-3 md:pl-1 md:pr-1 pl-2 pr-3 py-1.5 bg-gray-700 rounded-md shadow-sm flex items-center lg:gap-3 gap-1 w-full lg:flex-row md:flex-col flex-row">
                    <div className="flex-shrink-0 w-6 h-6 relative flex justify-center items-center">
                      <BiDollar color="white" size={22} />
                    </div>
                    <div className="text-white text-sm font-medium font-figtree leading-normal text-left whitespace-nowrap lg:block md:hidden block">
                      <NoTranslate>{t("dashboard.layout.item-13")}</NoTranslate>
                    </div>
                  </button>
                </a>
              )}
          </div>
        </div>
      </div>

      {show && (
        <div className="px-2">
          <LanguageChanger
            i18n={i18n}
            className={"inline-flex mt-auto bg-gray-700 mb-12"}
          />
        </div>
      )}

      <div className="self-stretch mt-auto px-2 pb-1 text-center lg:block hidden">
        <p className="text-xs text-gray-400">
          © SUM · 2021-{new Date().getFullYear()}
          <br />
          {process.env.REACT_APP_VERSION}
        </p>
      </div>
    </aside>
  );
};

type LanguageChangerProps = {
  i18n: i18n;
  className?: string;
};

export const LanguageChanger = ({ i18n, className }: LanguageChangerProps) => {
  const icons: { [key: string]: React.ComponentType<{ className?: string }> } =
    {
      en: US,
      "en-US": US,
      "en-CH": CH,
      "en-EU": GB,
      fr: FR,
      "fr-CH": CH,
      "fr-FR": FR,
    };

  const texts: { [key: string]: string } = {
    en: "English",
    "en-US": "English",
    "en-CH": "English",
    "en-EU": "English",
    fr: "Français",
    "fr-FR": "Français",
    "fr-CH": "Français",
  };

  const classes = `pl-1 pr-1.5 py-1 rounded-full justify-start items-center gap-0.5 cursor-pointer ${
    className ? className : "bg-white md:flex hidden"
  }`;

  const languageCode = getLanguageCode(i18n.language);
  const FlagIcon = icons[i18n.language] || icons[languageCode] || US;
  const [, setCookie] = useCookies([LANGUAGE_STORAGE_KEY]);

  return (
    <div className={classes}>
      <Dropdown
        label=""
        dismissOnClick={true}
        renderTrigger={() => (
          <div className="flex items-center px-2">
            <FlagIcon className="w-5 h-5" />
            <div
              className={
                className
                  ? "text-white ml-3"
                  : "text-gray-900 ml-3 bg-white md:flex hidden"
              }
            >
              <NoTranslate>
                {texts[i18n.language] || texts[languageCode] || texts["en-US"]}
              </NoTranslate>
            </div>
            <div className="w-4 h-4 relative">
              <HiChevronDown size={16} className="text-gray-400" />
            </div>
          </div>
        )}
      >
        {["en-US", "fr-FR"].map((lng) => (
          <Dropdown.Item
            key={lng}
            onClick={() => {
              const persistLanguage = (language: string) => {
                if (typeof window === "undefined") return;

                persistLanguageToStorage(language, true);

                const domain = getCookieDomain();
                setCookie(LANGUAGE_STORAGE_KEY, language, {
                  path: "/",
                  maxAge: 60 * 60 * 24 * 365,
                  sameSite: "none",
                  secure: true,
                  ...(domain ? { domain } : {}),
                });
              };

              persistLanguage(lng);
              void i18n
                .changeLanguage(lng)
                .then(() => persistLanguage(i18n.language));
            }}
          >
            <NoTranslate>{texts[lng]}</NoTranslate>
          </Dropdown.Item>
        ))}
      </Dropdown>
    </div>
  );
};

type AccountDropDownProps = {
  t: TFunction<"translation", undefined, "translation">;
  handleLogout: VoidFunction;
  logo: string;
  alt?: string;
  user: IUser;
};

const AccountDropDown = ({
  t,
  handleLogout,
  logo,
  alt,
  user,
}: AccountDropDownProps) => {
  const permissions = usePermissions(user);
  const isOwnerLike = !permissions.isRoleGoverned();

  return (
    <Dropdown
      label=""
      dismissOnClick={true}
      renderTrigger={() => (
        <div className="bg-white rounded-full justify-center items-center flex cursor-pointer">
          <img
            className="w-8 h-8 rounded-full"
            src={logo}
            alt={alt ?? "Account"}
          />
        </div>
      )}
      className="bg-gray-800 w-56"
    >
      <Dropdown.Item
        as={Link}
        to={"profile"}
        className="self-stretch px-4 py-2 justify-start items-center gap-3 inline-flex bg-gray-800 hover:bg-gray-700 focus:bg-gray-700 transition-colors"
      >
        <div className="w-5 h-5 relative">
          <HiUser size={20} className="text-gray-400" />
        </div>
        <span className="text-gray-100 text-sm font-normal font-figtree leading-tight">
          <NoTranslate>{t("dashboard.layout.item-05")}</NoTranslate>
        </span>
      </Dropdown.Item>
      {isOwnerLike && (
        <Dropdown.Item
          as={Link}
          to={"billing"}
          className="self-stretch px-4 py-2 justify-start items-center gap-3 inline-flex bg-gray-800 hover:bg-gray-700 focus:bg-gray-700 transition-colors"
        >
          <div className="w-5 h-5 relative">
            <HiCurrencyDollar size={20} className="text-gray-400" />
          </div>
          <span className="text-gray-100 text-sm font-normal font-figtree leading-tight">
            <NoTranslate>{t("dashboard.layout.item-08")}</NoTranslate>
          </span>
        </Dropdown.Item>
      )}

      <Dropdown.Item
        as={Link}
        to={"support"}
        className="self-stretch px-4 py-2 justify-start items-center gap-3 inline-flex bg-gray-800 hover:bg-gray-700 focus:bg-gray-700 transition-colors"
      >
        <div className="w-5 h-5 relative">
          <HiSupport size={20} className="text-gray-400" />
        </div>
        <span className="text-gray-100 text-sm font-normal font-figtree leading-tight">
          <NoTranslate>{t("dashboard.layout.item-06")}</NoTranslate>
        </span>
      </Dropdown.Item>

      <Dropdown.Divider className="bg-gray-600" />
      <Dropdown.Item
        onClick={handleLogout}
        className="self-stretch px-4 py-2 justify-start items-center gap-3 inline-flex bg-gray-800 hover:bg-gray-700 focus:bg-gray-700 transition-colors"
      >
        <div className="w-5 h-5 relative">
          <HiLogin size={20} className="text-gray-400" />
        </div>
        <span className="text-gray-100 text-sm font-normal font-figtree leading-tight">
          <NoTranslate>{t("dashboard.layout.btn-01")}</NoTranslate>
        </span>
      </Dropdown.Item>
    </Dropdown>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    customer_identification,
    user,
    setUser,
    onOffUpgradeNow,
    onOffUserEndedSubscription,
  } = useAuth();
  const permissions = usePermissions(user);
  useDashboardLanguage();

  const [error, setError] = useState<any>();
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [showSideMenu, setShowSideMenu] = useState(false);
  const [showLogOutPopUp, setShowLogOutPopUp] = useState(false);
  const [showHomePopUp, setShowHomePopUp] = useState(false);
  const [showPromoCodeModal, setShowPromoCodeModal] = useState(false);

  useEffect(() => {
    const openPlans = () => setShowPlansModal(true);
    window.addEventListener("openPlansModal", openPlans);
    return () => window.removeEventListener("openPlansModal", openPlans);
  }, []);

  const {
    isError,
    isLoading,
    isSuccess,
    data: verifyData,
    refetch,
  } = useGetVerifyPlans(customer_identification, user.entity_identification, {
    onSuccess: (value) => {
      if (!value) return;

      const borrowsOwnerPlan = value.borrows_owner_plan === true;

      if (borrowsOwnerPlan) {
        setIsNewUser(false);
        setUser({
          ...user,
          customer_top_plan: value.customer_top_plan,
          trial_left_days: value.trial_left_days,
          is_promo_trial: value.is_promo_trial || false,
          trial_restrictions: value.trial_restrictions,
          trial_end_date: value.trial_end_date,
        });
        onOffUserEndedSubscription((value.owner_plans || []).length <= 0);
        return;
      }

      if (value.is_new_user) {
        setShowPlansModal(true);
        setIsNewUser(true);
      } else {
        setIsNewUser(false);
        setUser({
          ...user,
          customer_top_plan: value.customer_top_plan,
          trial_left_days: value.trial_left_days,
          is_promo_trial: value.is_promo_trial || false,
          trial_restrictions: value.trial_restrictions,
          trial_end_date: value.trial_end_date,
        });
      }

      if (value.customer_plans?.length <= 0) {
        toast.warn(
          "You have no active plans. Please upgrade your subscription."
        );

        onOffUserEndedSubscription(true);
        setShowPlansModal(true);
      } else {
        onOffUserEndedSubscription(false);
      }
    },
    onError: (err: AxiosError<any, any>) => {
      if (
        err.response?.status === 402 ||
        err.response?.data?.code === "RSPYKO001"
      ) {
        setError(err);
      }
    },
  });

  const { isSuccess: isPrefSuccess } = useGetEntityPreferencesByPath(
    customer_identification,
    user.entity_identification,
    "password_validation_regex_level",
    {
      enabled: isSuccess,
      onError: (err: AxiosError<any, any>) => {
        if (!isNewUser) toast.error(err.response?.data?.message || err.message);
      },
      onSuccess(data) {
        try {
          const passwordLevel =
            data?.password_validation_regex_level || "level2";
          SecureStorage.setPasswordValidationLevel(passwordLevel);
        } catch (error) {
          console.error("Error setting password validation level:", error);
        }
      },
    }
  );

  const { data: logoRes, isSuccess: isLogoSuccess } =
    useGetEntityPreferencesByPath(
      customer_identification,
      user.entity_identification,
      "cdn_logo_project_path",
      {
        enabled: isSuccess,
        onError: () => {},
      }
    );

  const upgradeNowHandler = () => {
    onOffUpgradeNow(true);
    navigate("billing", { replace: true });
  };

  useEffect(() => {
    const entityId = user?.entity_identification;
    if (!entityId) return;

    const params = new URLSearchParams(location.search);
    const urlStatus = params.get("status") === "true";
    const urlPlan = params.get("plan_name");

    if (urlStatus && urlPlan) {
      trackSubscriptionConversion({
        planName: urlPlan,
        entityId,
        externalId: customer_identification,
        isNewSignup: !!verifyData?.is_new_user,
        value: /UMD_FREE/i.test(urlPlan) ? 0 : undefined,
      });
    }
  }, [
    location.search,
    user?.entity_identification,
    verifyData?.is_new_user,
    customer_identification,
  ]);

  const panelViewTracked = useRef(false);
  useEffect(() => {
    if (panelViewTracked.current) return;
    panelViewTracked.current = true;
    trackPanelView();
  }, []);

  useEffect(() => {
    if (isSuccess) {
      if (verifyData?.borrows_owner_plan === true) return;

      if (verifyData?.is_new_user && !showPlansModal) {
        toast.warn(
          "You have no active plans. Please upgrade your subscription."
        );

        setShowPlansModal(true);
        setIsNewUser(true);
      }

      if (verifyData?.customer_plans?.length <= 0 && !showPlansModal) {
        toast.warn(
          "You have no active plans. Please upgrade your subscription."
        );

        onOffUserEndedSubscription(true);
        setShowPlansModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSuccess,
    location,
    onOffUserEndedSubscription,
    verifyData?.customer_plans?.length,
    verifyData?.is_new_user,
    verifyData?.borrows_owner_plan,
  ]);

  let content;

  if (isLoading)
    content = (
      <div className="container mx-auto py-6">
        Loading...
        <AiOutlineLoading className="h-6 w-6 animate-spin ml-2 inline-flex" />
      </div>
    );

  if (isError)
    content = (
      <div className="container mx-auto py-6">
        {!error ? (
          <CustomButton
            variant="primary"
            onClick={() => refetch()}
            title="Refetch"
          />
        ) : (
          <AlertMessage color="warning">
            Your subscription is ended.
            <CustomButton
              variant="warning"
              size="sm"
              className="mr-2"
              onClick={() => setShowPlansModal(true)}
              title="Buy a plan"
            />
          </AlertMessage>
        )}
      </div>
    );

  if (isSuccess && isPrefSuccess && isLogoSuccess) content = children;

  return (
    <div className="dashboard-v2">
      <SideBarMenu
        t={t}
        i18n={i18n}
        user={user}
        handleUpgrade={upgradeNowHandler}
        show={showSideMenu}
        handleHideSidebar={() => setShowSideMenu(false)}
        navigate={navigate}
        customer_identification={customer_identification}
        entityIdentification={user.entity_identification}
      />

      <main
        className="lg:w-[calc(100% - 320px)] md:w-[calc(100% - 80px)] w-full bg-gray-100"
        id="content-area"
      >
        <ImpersonationBanner />

        <header className="sticky top-0 z-50">
          <div className="w-full h-16 px-4 bg-white border-b border-gray-200 justify-start items-center gap-4 inline-flex">
            <button
              data-testid="sidebar-mobile-toggle"
              aria-label="Open menu"
              className="h-full md:px-4 pr-4 justify-center items-center inline-flex border-r border-gray-100 md:hidden"
              onClick={() => setShowSideMenu(true)}
            >
              <div className="w-6 h-6 relative">
                <HiOutlineViewGridAdd size={24} className="text-gray-500" />
              </div>
            </button>
            {permissions.can(Cap.SEARCH, Level.VIEW) ? (
              <DlSearch />
            ) : (
              <div className="grow" />
            )}
            <div className="justify-start items-center gap-3 flex shrink-0">
              <LanguageChanger i18n={i18n} />
              <AccountDropDown
                t={t}
                handleLogout={() => setShowLogOutPopUp(true)}
                logo={logoRes?.cdn_logo_project_path}
                user={user}
              />
            </div>
          </div>
        </header>

        {content}
      </main>

      <ModalShell
        show={showPlansModal}
        onClose={() => setShowPlansModal(false)}
        size="7xl"
        title={t("dashboard.billings.plansModal.title")}
        footer={
          <CustomButton
            onClick={() => setShowPlansModal(false)}
            title={t("common.close") as string}
          />
        }
      >
        <Plans
          user_identification={customer_identification}
          entity_identification={user.entity_identification}
          isInBilling={false}
          onCloseModal={() => setShowPlansModal(false)}
        />
      </ModalShell>

      <ModalShell
        show={showHomePopUp}
        onClose={() => setShowHomePopUp(false)}
        footer={
          <>
            <CustomButton
              variant="secondary"
              href="/contact-us"
              onClick={() => setShowHomePopUp(false)}
              title="Contact us"
            />
            <CustomButton
              variant="primary"
              href="/control-panel/billing"
              onClick={() => setShowHomePopUp(false)}
              title="Upgrade now"
            />
          </>
        }
      >
        <div className="flex flex-col items-center justify-center text-center">
          <img
            title="Super User Manager"
            loading="lazy"
            src={"https://cdn.1cdn.app/application/UMD/umd_logout.svg"}
            className="mb-6"
            alt="logout logo"
            width={170}
            height={150}
          />

          <h2 className="text-lg font-semibold text-gray-900">
            You have canceled your trial plan
          </h2>

          <p className="mt-0.5 text-sm text-gray-500">
            Two ways to keep enjoying Super User Manager dashboard:
          </p>
        </div>
      </ModalShell>

      <ModalShell
        show={showLogOutPopUp}
        onClose={() => setShowLogOutPopUp(false)}
        position={"center"}
        dismissible
        footer={
          <>
            <CustomButton
              onClick={() => setShowLogOutPopUp(false)}
              title={t("common.cancel") as string}
              variant="secondary"
            />
            <CustomButton
              href="/logout"
              variant="danger"
              title={t("dashboard.layout.btn-01") as string}
            />
          </>
        }
      >
        <div className="grid grid-rows-[auto_1fr] justify-items-center gap-4 text-center">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-red-100">
            <HiLogin size={24} className="text-red-600" />
          </div>
          <div className="space-y-2">
            <div className="break-words text-lg font-semibold leading-7 text-gray-900">
              {t("dashboard.layout.text-01")}
            </div>
            <div className="break-words text-sm leading-tight text-gray-500">
              {t("dashboard.layout.text-02")}
            </div>
          </div>
        </div>
      </ModalShell>

      <PromoCodeModal
        showModal={showPromoCodeModal}
        setShowModal={setShowPromoCodeModal}
        onSubmit={() => {
          refetch();
        }}
      />
    </div>
  );
};

export default Layout;
